import { useState } from 'react';

import {
  type ConsentState,
  useCookieConsent,
} from 'contexts/CookieConsentContext';

import Button from '@polyai/ui/components/atoms/Button';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  ConfirmationFilledIcon,
  NoAccessIcon,
} from '@polyai/ui/components/atoms/Icons';
import Text from '@polyai/ui/components/atoms/Text';

import CookieSettingsModal from 'components/molecules/CookieSettingsModal';

const COOKIE_CATEGORIES: {
  key: keyof ConsentState;
  label: string;
  alwaysEnabled?: boolean;
}[] = [
  { key: 'necessary', label: 'Necessary', alwaysEnabled: true },
  { key: 'preferences', label: 'Preferences' },
  { key: 'statistics', label: 'Statistics' },
  { key: 'marketing', label: 'Marketing' },
];

const CookiesTabContent: React.FC = () => {
  const { consent, consentDetails, withdrawConsent, isConsentReady } =
    useCookieConsent();
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(true);
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);

  const formatConsentDate = (dateString: string) => {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) {
      return dateString;
    }
    return date.toLocaleString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const handleWithdraw = () => {
    withdrawConsent();
  };

  const handleChangeConsent = () => {
    setIsSettingsModalOpen(true);
  };

  return (
    <>
      <div className="flex flex-col gap-sm4">
        <Text color="textSecondary" size="body.small">
          We use cookies to understand how the platform is used and to improve
          your experience. You can update your preferences at any time
        </Text>

        <div className="flex flex-col gap-sm2">
          <Text size="body.small">Your current state</Text>

          <div className="flex flex-col gap-sm2">
            {COOKIE_CATEGORIES.map(({ key, label, alwaysEnabled }) => {
              const isEnabled = alwaysEnabled || consent[key];
              return (
                <div key={key} className="flex items-center gap-xs1">
                  {isEnabled ? (
                    <ConfirmationFilledIcon color="iconSuccess" size="sm" />
                  ) : (
                    <NoAccessIcon color="textSecondary" size="sm" />
                  )}
                  <Text size="body.regular" weight="semibold">
                    {label}
                  </Text>
                </div>
              );
            })}
          </div>
        </div>

        {isConsentReady && consentDetails && (
          <div className="flex flex-col gap-sm2 px-sm2 pt-sm1 pb-sm2 border border-border-subtle rounded-medium">
            <button
              aria-expanded={isAdvancedOpen}
              aria-label="Advanced details"
              className="flex items-center justify-between w-full p-0 bg-transparent border-none cursor-pointer"
              type="button"
              onClick={() => setIsAdvancedOpen(!isAdvancedOpen)}
            >
              <Text size="body.regular" weight="semibold">
                Advanced details
              </Text>
              {isAdvancedOpen ? (
                <ChevronUpIcon aria-hidden="true" size="sm" />
              ) : (
                <ChevronDownIcon aria-hidden="true" size="sm" />
              )}
            </button>

            {isAdvancedOpen && (
              <div className="flex flex-col gap-sm3">
                <div className="flex flex-col gap-xs1">
                  <Text color="textSecondary" size="body.small">
                    Consent date
                  </Text>
                  <Text size="body.regular">
                    {formatConsentDate(consentDetails.consentDate)}
                  </Text>
                </div>

                <div className="flex flex-col gap-xs1">
                  <Text color="textSecondary" size="body.small">
                    Your consent ID
                  </Text>
                  <Text
                    className="break-all font-monospace"
                    size="body.regular"
                  >
                    {consentDetails.consentId}
                  </Text>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="flex gap-sm1 justify-end mt-sm1 mb-sm4">
          <Button
            label="Withdraw your consent"
            variant="GHOST"
            onClick={handleWithdraw}
          />
          <Button
            label="Change your consent"
            variant="SECONDARY"
            onClick={handleChangeConsent}
          />
        </div>
      </div>

      <CookieSettingsModal
        open={isSettingsModalOpen}
        onOpenChange={setIsSettingsModalOpen}
      />
    </>
  );
};

export default CookiesTabContent;
